import { createElement } from 'react';
import { createTheme, type Theme } from '@mui/material/styles';
import { ChevronDown } from './icons';
import { WS_COLOR, WS_CONTROL, WS_FONT, WS_RADIUS, WS_STATUS, WS_TYPE } from './tokens';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    /** Tonal secondary button (DESIGN.md): one filled primary per row, the rest tonal. */
    tonal: true;
  }
}

/** The select caret: Lucide, like every other icon. */
function SelectChevron(props: { className?: string }) {
  return createElement(ChevronDown, { size: 18, strokeWidth: 1.75, className: props.className });
}

/**
 * The one focus ring (DESIGN.md accessibility rule): 2px in the focus ink, 2px off the edge.
 * Applied wherever the workspace theme is mounted, with or without the page baseline, and
 * never to anything outside it (see WorkspaceThemeProvider and the MuiModal override below).
 */
export const focusRing = { outline: `2px solid ${WS_COLOR.focus}`, outlineOffset: 2 } as const;

const t = (key: keyof typeof WS_TYPE) => ({
  fontSize: `${WS_TYPE[key][0]}px`,
  lineHeight: `${WS_TYPE[key][1]}px`,
  fontWeight: WS_TYPE[key][2],
});

/**
 * The Decision Workspace theme (DESIGN.md), light only.
 *
 * LIGHT ONLY — the owner's 2026-08-02 ruling stands. DESIGN.md defines dark through the
 * same semantic roles, but nothing here ships a dark palette until that ruling is revisited.
 *
 * It is a separate theme from `createPlatformTheme` on purpose: the ported screens still run
 * on the Classic theme, and switching them is a per-pattern decision the owner makes, not a
 * side effect of adding this file.
 */
export function createWorkspaceTheme(): Theme {
  return createTheme({
    palette: {
      mode: 'light',
      primary: { main: WS_COLOR.primary, contrastText: WS_COLOR.onPrimary },
      success: { main: WS_STATUS.success.ink },
      warning: { main: WS_STATUS.warning.ink },
      error: { main: WS_STATUS.error.ink },
      info: { main: WS_STATUS.info.ink },
      background: { default: WS_COLOR.background, paper: WS_COLOR.surface },
      divider: WS_COLOR.border,
      text: { primary: WS_COLOR.text, secondary: WS_COLOR.textMuted },
    },
    spacing: 4,
    shape: { borderRadius: WS_RADIUS.field },
    typography: {
      fontFamily: WS_FONT.sans,
      h1: { ...t('pageTitle'), letterSpacing: 0 },
      h2: { ...t('sectionTitle') },
      h3: { ...t('sectionTitle') },
      body1: { ...t('body') },
      body2: { ...t('table') },
      button: { ...t('label'), textTransform: 'none', letterSpacing: 0 },
      caption: { ...t('meta') },
      overline: { ...t('meta'), textTransform: 'none', letterSpacing: 0 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { WebkitFontSmoothing: 'antialiased', fontVariantNumeric: 'normal' },
          // Focus is always visible (DESIGN.md accessibility rule).
          ':focus-visible': focusRing,
        },
      },
      // Dialogs, sheets, menus and popovers render in a portal, outside the provider's wrapper
      // element, so they carry the ring themselves.
      MuiModal: { styleOverrides: { root: { '& :focus-visible': focusRing } } },
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: false },
        styleOverrides: {
          root: {
            height: WS_CONTROL.height,
            borderRadius: WS_RADIUS.button,
            paddingInline: 20,
            minWidth: 44,
            whiteSpace: 'nowrap',
          },
          text: { paddingInline: 12 },
        },
        variants: [
          {
            props: { variant: 'tonal' },
            style: {
              backgroundColor: WS_COLOR.tonal,
              color: WS_COLOR.onTonal,
              '&:hover': { backgroundColor: WS_COLOR.tonalHover },
              '&.Mui-disabled': { opacity: 0.45, color: WS_COLOR.onTonal },
            },
          },
        ],
      },
      // Small, so the label's resting position fits the 36px control (the default assumes 56px and
      // sits on the bottom edge of a shorter one).
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            height: WS_CONTROL.height,
            // A multiline field grows with its rows; only the single-line ones are 36px.
            '&.MuiInputBase-multiline': { height: 'auto', minHeight: WS_CONTROL.height },
            borderRadius: WS_RADIUS.field,
            backgroundColor: WS_COLOR.surface,
            fontSize: WS_TYPE.table[0],
            '& .MuiOutlinedInput-notchedOutline': { borderColor: WS_COLOR.fieldOutline },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: WS_COLOR.text },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: WS_COLOR.focus,
              borderWidth: 2,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: WS_RADIUS.chip, height: 24, ...t('meta'), fontWeight: 500 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: 40,
            paddingInline: 16,
            ...t('table'),
            fontWeight: 500,
          },
        },
      },
      MuiTabs: {
        // Tabs scroll sideways instead of clipping when the layout is narrow.
        defaultProps: {
          variant: 'scrollable',
          scrollButtons: 'auto',
          allowScrollButtonsMobile: true,
        },
        styleOverrides: { root: { minHeight: 40 } },
      },
      MuiDialog: { styleOverrides: { paper: { borderRadius: WS_RADIUS.dialog } } },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: WS_RADIUS.card,
            border: `1px solid ${WS_COLOR.border}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiTooltip: {
        defaultProps: { arrow: true, enterDelay: 400 },
        styleOverrides: {
          tooltip: {
            backgroundColor: WS_COLOR.text,
            color: WS_COLOR.surface,
            ...t('meta'),
            borderRadius: 6,
            padding: '6px 10px',
          },
          arrow: { color: WS_COLOR.text },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            width: WS_CONTROL.height,
            height: WS_CONTROL.height,
            color: WS_COLOR.textMuted,
            position: 'relative',
            // Visual 36px, hit area 44px (DESIGN.md accessibility rule).
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: -(WS_CONTROL.hitArea - WS_CONTROL.height) / 2,
            },
          },
        },
      },
      MuiCheckbox: {
        defaultProps: { disableRipple: false },
        styleOverrides: { root: { padding: 8, color: WS_COLOR.fieldOutline } },
      },
      MuiRadio: { styleOverrides: { root: { padding: 8, color: WS_COLOR.fieldOutline } } },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { '&:not(.Mui-checked) + .MuiSwitch-track': { opacity: 1 } },
          track: { backgroundColor: WS_COLOR.fieldOutline, opacity: 1 },
          thumb: { boxShadow: 'none' },
        },
      },
      MuiSelect: { defaultProps: { IconComponent: SelectChevron } },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: WS_RADIUS.card,
            border: `1px solid ${WS_COLOR.border}`,
            boxShadow: WS_COLOR.menuShadow,
          },
          list: { padding: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: WS_CONTROL.height,
            borderRadius: 8,
            ...t('table'),
            '&.Mui-selected': {
              backgroundColor: WS_STATUS.info.fill,
              color: WS_STATUS.info.ink,
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 4, borderRadius: 2, backgroundColor: WS_COLOR.surfaceSubtle },
        },
      },
      MuiBackdrop: { styleOverrides: { root: { backgroundColor: WS_COLOR.scrim } } },
    },
  });
}
